import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, Database, RefreshCw } from "lucide-react";

const FILTERS = [["all", "All"], ["ezap", "EZAP"], ["wp", "WP"], ["deep_research", "Deep Research"], ["latest_update", "Latest Update"]];
const SRC_STYLE = {
  EZAP: "bg-emerald-600 text-white",
  WP: "bg-slate-200 text-slate-800",
  "Deep Research": "bg-blue-100 text-blue-800",
  "Latest Update": "bg-amber-100 text-amber-800",
};
const STATUS_STYLE = { Official: "text-emerald-700", Tentative: "text-amber-700", TBA: "text-slate-500", Conflict: "text-rose-700" };

const fmt = (ts) => (ts ? new Date(ts).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export function ResearchHistory() {
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(false);

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const { data: d } = await client.get("/research-history", { params: { source: f, limit: 150 } });
      setData(d);
    } catch (e) {
      toast.error(apiErr(e, "Could not load research history"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(filter); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section data-testid="research-history" className="mt-12">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Research performed</p>
          <h2 className="font-serif text-lg font-bold text-slate-900">Research history</h2>
          <p className="text-xs text-slate-500 mt-1">Only research that actually ran is listed — no calls are made to populate this view. EZAP rows are the one-off Gemini exception research for 2027 Basic Details ({data.ezap_credits_total ?? 0} credits total).</p>
        </div>
        <button data-testid="research-history-refresh" onClick={() => load()} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-blue-500">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="research-history-filters">
        {FILTERS.map(([k, label]) => (
          <button key={k} data-testid={`research-filter-${k}`} onClick={() => setFilter(k)}
            className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}>
            {label} <span className="opacity-70">{data.counts?.[k] ?? ""}</span>
          </button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full text-xs" data-testid="research-history-table">
          <thead className="bg-slate-50 text-left text-slate-500 uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2">Source</th><th className="px-3 py-2">Exam / post</th><th className="px-3 py-2">Tab / field</th><th className="px-3 py-2">Question</th>
              <th className="px-3 py-2">Model</th><th className="px-3 py-2">Sources</th><th className="px-3 py-2">Extracted value</th><th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Conf.</th><th className="px-3 py-2">Credits</th><th className="px-3 py-2">Cache</th><th className="px-3 py-2">When</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-6 text-center text-slate-500" data-testid="research-history-empty">{loading ? "Loading…" : "No research recorded for this filter."}</td></tr>
            )}
            {data.items.map((r) => <HistoryRow key={`${r.source}-${r.id}`} r={r} />)}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function HistoryRow({ r }) {
  const ezap = r.source === "EZAP";
  return (
    <tr data-testid={`research-row-${r.source.toLowerCase().replace(/\s+/g, "-")}`} className={`border-t border-slate-100 align-top ${ezap ? "bg-emerald-50/40" : ""}`}>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-semibold ${SRC_STYLE[r.source] || "bg-slate-100"}`}>{ezap && <Database className="h-3 w-3" />}{r.source}</span>
        <div className="text-[10px] text-slate-500 mt-1 max-w-[160px]">{r.label}</div>
      </td>
      <td className="px-3 py-2 max-w-[180px] break-words font-medium text-slate-800">{r.subject}</td>
      <td className="px-3 py-2 whitespace-nowrap text-slate-600">{r.field || "—"}</td>
      <td className="px-3 py-2 max-w-[260px] text-slate-600" title={r.question}>{(r.question || "").slice(0, 140)}{(r.question || "").length > 140 ? "…" : ""}</td>
      <td className="px-3 py-2 whitespace-nowrap font-mono text-slate-600">{r.model || "—"}</td>
      <td className="px-3 py-2 max-w-[220px]">
        {(r.urls || []).slice(0, 4).map((u) => (
          <a key={u} href={u} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-700 hover:underline truncate"><ExternalLink className="h-3 w-3 shrink-0" />{u.replace(/^https?:\/\/(www\.)?/, "").slice(0, 48)}</a>
        ))}
        {(r.urls || []).length === 0 && <span className="text-slate-400">no source</span>}
      </td>
      <td className="px-3 py-2 max-w-[200px] text-slate-800">{r.value || <span className="text-slate-400">—</span>}{ezap && r.evidence && <div className="text-[10px] text-slate-500 mt-1 italic">“{r.evidence.slice(0, 120)}”</div>}</td>
      <td className={`px-3 py-2 whitespace-nowrap font-semibold ${STATUS_STYLE[r.status] || "text-slate-700"}`}>{r.status || "—"}</td>
      <td className="px-3 py-2 whitespace-nowrap">{r.confidence != null ? `${Math.round(r.confidence * 100)}%` : "—"}</td>
      <td className="px-3 py-2 whitespace-nowrap">{r.credits != null ? Number(r.credits).toFixed(3) : "—"}</td>
      <td className="px-3 py-2 whitespace-nowrap">{ezap ? (r.reuse_count ? `reused ×${r.reuse_count}` : "fresh") : "—"}</td>
      <td className="px-3 py-2 whitespace-nowrap text-slate-500">{fmt(r.timestamp)}</td>
    </tr>
  );
}
